import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Bolt as BoltIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { apiMaster } from '@/api/platform';
import { StatusPill } from '@/components/StatusPill';
import { useToast } from '@/components/Toaster';
import type { ApiMaster, Tenant, TenantApiAccess } from '@/types/backend';

/**
 * Subscription / API access editor for one tenant.
 *
 * Layered, top-down:
 *
 *   1. Subscription tier — preset that ALLOWs every api_master row in
 *      the chosen milestones and clears everything else.
 *
 *   2. Milestone groups — one accordion per milestone (M1 / M2 / M3 /
 *      AUTH / ADMIN / TENANT). Each group has a tri-state "Select all"
 *      checkbox and per-row checkboxes. A "Reset to defaults" button
 *      drops every override in that group so APIs fall back to
 *      `ApiMaster.defaultEnabled`.
 *
 * Effective access for any API is: explicit override (ALLOW / DENY) →
 * milestone preset → `ApiMaster.defaultEnabled`. Saving applies the
 * diff against the existing rules: PUT for new/changed overrides,
 * DELETE for cleared ones.
 */

type Effective = 'ALLOW' | 'DENY';
type Tier = 'M1' | 'M1_M3' | 'FULL' | 'CUSTOM';

const TIER_LABELS: Record<Tier, string> = {
  M1: 'M1 only',
  M1_M3: 'M1 + M3',
  FULL: 'M1 + M2 + M3',
  CUSTOM: 'Custom',
};

const TIER_MILESTONES: Record<Exclude<Tier, 'CUSTOM'>, Set<string>> = {
  M1: new Set(['AUTH', 'M1']),
  M1_M3: new Set(['AUTH', 'M1', 'M3']),
  FULL: new Set(['AUTH', 'M1', 'M2', 'M3']),
};

const ALL_TIERS: Tier[] = ['M1', 'M1_M3', 'FULL', 'CUSTOM'];

/** Stable ordering so M1 always comes above M2 etc. */
const MILESTONE_ORDER = ['AUTH', 'M1', 'M2', 'M3', 'ADMIN', 'TENANT'];

export function TenantApiAccessDialog({
  tenant,
  open,
  onClose,
  onBack,
}: {
  tenant: Tenant | null;
  open: boolean;
  onClose: () => void;
  /** When set, replaces the "Cancel" button with a "Back" button that returns to the previous dialog. */
  onBack?: () => void;
}) {
  const theme = useTheme();
  const toast = useToast();

  const { data: catalog, isLoading: loadingCatalog } = useSWR<ApiMaster[]>(
    open ? '/api/abdm/admin/api-master' : null,
    () => apiMaster.list(),
  );
  const accessKey = open && tenant ? ['tenant-api-access', tenant.tenantId] as const : null;
  const { data: rules, isLoading: loadingRules, mutate: refetchRules } = useSWR<TenantApiAccess[]>(
    accessKey,
    () => apiMaster.tenantAccess(tenant!.tenantId),
  );

  const [overrides, setOverrides] = useState<Map<string, Effective>>(new Map());
  const [tier, setTier] = useState<Tier>('CUSTOM');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  // Hydrate from server rules whenever the dialog opens.
  useEffect(() => {
    if (!open || !rules || !catalog) return;
    const m = new Map<string, Effective>();
    rules.forEach((r) => m.set(r.apiCode, r.access === 'DENY' ? 'DENY' : 'ALLOW'));
    setOverrides(m);
    setTier(detectTier(rules, catalog));
    setSearch('');
    // Auto-expand the milestones the tenant has any access to.
    const exp: Record<string, boolean> = {};
    catalog.forEach((r) => {
      const isAllowed = (m.get(r.apiCode) ?? (r.defaultEnabled ? 'ALLOW' : 'DENY')) === 'ALLOW';
      if (isAllowed) exp[r.milestone] = true;
    });
    setExpanded(exp);
  }, [open, rules, catalog]);

  // Group catalog by milestone, ordered.
  const grouped = useMemo(() => {
    const map = new Map<string, ApiMaster[]>();
    (catalog ?? []).forEach((r) => {
      const arr = map.get(r.milestone) ?? [];
      arr.push(r);
      map.set(r.milestone, arr);
    });
    map.forEach((arr) => arr.sort((a, b) => a.apiCode.localeCompare(b.apiCode)));
    return [...map.entries()].sort(([a], [b]) => {
      const ai = MILESTONE_ORDER.indexOf(a);
      const bi = MILESTONE_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [catalog]);

  /** Effective state of a row given current local overrides. */
  const effectiveOf = (api: ApiMaster): Effective =>
    overrides.get(api.apiCode) ?? (api.defaultEnabled ? 'ALLOW' : 'DENY');

  // Apply a tier preset → seed overrides for every row.
  const applyTier = (next: Tier) => {
    setTier(next);
    if (next === 'CUSTOM') return;
    const allowed = TIER_MILESTONES[next];
    const m = new Map<string, Effective>();
    (catalog ?? []).forEach((r) => {
      m.set(r.apiCode, allowed.has(r.milestone) ? 'ALLOW' : 'DENY');
    });
    setOverrides(m);
  };

  const setOne = (apiCode: string, value: Effective) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(apiCode, value);
      return next;
    });
    setTier('CUSTOM');
  };

  /** Select / deselect every row in a milestone group. */
  const setMilestone = (milestone: string, value: Effective | 'RESET') => {
    setOverrides((prev) => {
      const next = new Map(prev);
      (catalog ?? []).filter((r) => r.milestone === milestone).forEach((r) => {
        if (value === 'RESET') next.delete(r.apiCode);
        else next.set(r.apiCode, value);
      });
      return next;
    });
    setTier('CUSTOM');
  };

  // Group-level tri-state. checked=all allowed, indeterminate=mixed, unchecked=none.
  const groupState = (rows: ApiMaster[]): { checked: boolean; indeterminate: boolean; allowedCount: number } => {
    let allowed = 0;
    rows.forEach((r) => { if (effectiveOf(r) === 'ALLOW') allowed++; });
    return {
      allowedCount: allowed,
      checked: allowed === rows.length && rows.length > 0,
      indeterminate: allowed > 0 && allowed < rows.length,
    };
  };

  const counts = useMemo(() => {
    let allow = 0;
    let deny = 0;
    overrides.forEach((v) => {
      if (v === 'ALLOW') allow++;
      else if (v === 'DENY') deny++;
    });
    return { allow, deny, total: catalog?.length ?? 0 };
  }, [overrides, catalog]);

  // Filter rows by search (within each group).
  const filterRows = (rows: ApiMaster[]) => {
    const lc = search.trim().toLowerCase();
    if (!lc) return rows;
    return rows.filter(
      (r) =>
        r.apiCode.toLowerCase().includes(lc) ||
        r.name.toLowerCase().includes(lc) ||
        (r.pathPattern ?? '').toLowerCase().includes(lc) ||
        (r.description ?? '').toLowerCase().includes(lc),
    );
  };

  const onSave = async () => {
    if (!tenant) return;
    setSaving(true);
    try {
      const before = new Map<string, 'ALLOW' | 'DENY'>();
      (rules ?? []).forEach((r) => before.set(r.apiCode, r.access === 'DENY' ? 'DENY' : 'ALLOW'));

      const allCodes = new Set<string>([...before.keys(), ...overrides.keys()]);
      const writes: Promise<unknown>[] = [];

      allCodes.forEach((code) => {
        const want = overrides.get(code);
        const have = before.get(code);
        if (want === have) return;
        if (!want) {
          writes.push(apiMaster.deleteTenantAccess(tenant.tenantId, code));
        } else {
          writes.push(apiMaster.setTenantAccess(tenant.tenantId, { apiCode: code, access: want }));
        }
      });

      if (writes.length === 0) {
        toast.success('No changes to save.');
        onClose();
        return;
      }
      await Promise.all(writes);
      await refetchRules();
      toast.success(
        `Saved API access for ${tenant.organizationName}`,
        `${writes.length} change${writes.length === 1 ? '' : 's'} applied`,
      );
      onClose();
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      toast.error('Save failed', detail?.message ?? detail?.error ?? (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const isLoading = loadingCatalog || loadingRules;

  return (
    <Dialog
      open={open && !!tenant}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <BoltIcon fontSize="small" sx={{ color: theme.palette.brand.main }} />
          <Stack spacing={0}>
            <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>
              API access · {tenant?.organizationName ?? '—'}
            </Typography>
            <Typography variant="caption" color="text.tertiary">
              Pick a subscription tier or tick the APIs each milestone should expose.
            </Typography>
          </Stack>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          {/* Tier presets */}
          <Box
            sx={{
              p: 2,
              borderRadius: theme.radius.sm + 'px',
              border: `1px solid ${theme.palette.border.subtle}`,
              bgcolor: theme.palette.surface.sunken,
            }}
          >
            <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>
              Subscription tier
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={tier}
              onChange={(_, v: Tier | null) => v && applyTier(v)}
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              {ALL_TIERS.map((t) => (
                <ToggleButton key={t} value={t} sx={{ textTransform: 'none', fontWeight: 500 }}>
                  {TIER_LABELS[t]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.tertiary" sx={{ mt: 1, display: 'block' }}>
              Tier presets restage every row; ticking individual APIs below switches you to "Custom".
            </Typography>
          </Box>

          {/* Search + counters */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              size="small"
              label="Search APIs"
              placeholder="apiCode, name, path…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 260, flex: 1 }}
              InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.tertiary' }} /> }}
            />
            <Stack direction="row" spacing={1}>
              <Chip size="small" label={`${counts.allow} Allow`} color="success" variant="outlined" />
              <Chip size="small" label={`${counts.deny} Deny`} color="error" variant="outlined" />
              <Chip
                size="small"
                label={`${counts.total - counts.allow - counts.deny} default`}
                variant="outlined"
              />
            </Stack>
          </Stack>

          {/* Milestone-grouped accordions */}
          {isLoading ? (
            <Typography variant="body2" color="text.secondary">Loading catalog…</Typography>
          ) : (
            <Stack spacing={1}>
              {grouped.map(([milestone, rows]) => {
                const visible = filterRows(rows);
                if (search && visible.length === 0) return null; // hide empty groups during search
                const state = groupState(rows);
                return (
                  <Accordion
                    key={milestone}
                    expanded={!!expanded[milestone] || !!search}
                    onChange={(_, exp) => setExpanded((prev) => ({ ...prev, [milestone]: exp }))}
                    disableGutters
                    sx={{
                      borderRadius: theme.radius.sm + 'px !important',
                      border: `1px solid ${theme.palette.border.subtle}`,
                      '&::before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ px: 2, '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' } }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                        <Checkbox
                          size="small"
                          checked={state.checked}
                          indeterminate={state.indeterminate}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(_, checked) => setMilestone(milestone, checked ? 'ALLOW' : 'DENY')}
                          sx={{ p: 0.5 }}
                        />
                        <StatusPill tone={milestoneTone(milestone)} dot={false}>{milestone}</StatusPill>
                        <Typography variant="body2" fontWeight={500}>
                          {milestoneTitle(milestone)}
                        </Typography>
                        <Typography variant="caption" color="text.tertiary">
                          {state.allowedCount} of {rows.length} allowed
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Allow every API in this milestone">
                            <Button size="small" onClick={() => setMilestone(milestone, 'ALLOW')} sx={{ fontSize: 11, textTransform: 'none' }}>
                              Select all
                            </Button>
                          </Tooltip>
                          <Tooltip title="Deny every API in this milestone">
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setMilestone(milestone, 'DENY')}
                              sx={{ fontSize: 11, textTransform: 'none' }}
                            >
                              Deny all
                            </Button>
                          </Tooltip>
                          <Tooltip title="Drop overrides — fall back to ApiMaster defaults">
                            <Button
                              size="small"
                              onClick={() => setMilestone(milestone, 'RESET')}
                              sx={{ fontSize: 11, textTransform: 'none' }}
                            >
                              Reset
                            </Button>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, borderTop: `1px solid ${theme.palette.border.subtle}` }}>
                      <Stack divider={<Box sx={{ borderBottom: `1px solid ${theme.palette.border.subtle}` }} />}>
                        {visible.map((api) => {
                          const eff = effectiveOf(api);
                          const overridden = overrides.has(api.apiCode);
                          return (
                            <Stack
                              key={api.apiCode}
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                              sx={{
                                px: 2,
                                py: 1,
                                bgcolor: eff === 'ALLOW' ? 'transparent' : theme.palette.surface.sunken,
                                opacity: eff === 'ALLOW' ? 1 : 0.85,
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={eff === 'ALLOW'}
                                onChange={(_, checked) => setOne(api.apiCode, checked ? 'ALLOW' : 'DENY')}
                                sx={{ p: 0.5 }}
                              />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 500, fontSize: 12 }}
                                  >
                                    {api.apiCode}
                                  </Typography>
                                  <Typography variant="caption" color="text.tertiary" noWrap>{api.name}</Typography>
                                </Stack>
                                {api.pathPattern && (
                                  <Typography
                                    variant="caption"
                                    color="text.tertiary"
                                    sx={{ fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all' }}
                                  >
                                    {api.httpMethod} {api.pathPattern}
                                  </Typography>
                                )}
                              </Box>
                              {api.defaultEnabled && (
                                <Tooltip title="Default-on for every tenant">
                                  <StatusPill tone="info" dot={false}>default-on</StatusPill>
                                </Tooltip>
                              )}
                              {overridden && (
                                <Tooltip title="Drop this override (use ApiMaster default)">
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setOverrides((prev) => {
                                        const next = new Map(prev);
                                        next.delete(api.apiCode);
                                        return next;
                                      });
                                    }}
                                    sx={{ fontSize: 11, textTransform: 'none', minWidth: 0 }}
                                  >
                                    Reset
                                  </Button>
                                </Tooltip>
                              )}
                            </Stack>
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}

          <Alert severity="info" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
            Each row's tick = ALLOW; unticked = DENY. <strong>Reset</strong> drops the override so the
            API falls back to <code>ApiMaster.defaultEnabled</code>. Nothing saves until you click <strong>Save changes</strong>.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onBack ? (
          <Button onClick={onBack} disabled={saving} startIcon={<ArrowBackIcon fontSize="small" />}>
            Back
          </Button>
        ) : (
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
        )}
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving || isLoading}
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function milestoneTone(m: string): 'info' | 'warning' | 'success' | 'mono' {
  if (m === 'M1') return 'info';
  if (m === 'M2') return 'warning';
  if (m === 'M3') return 'success';
  return 'mono';
}

function milestoneTitle(m: string): string {
  switch (m) {
    case 'AUTH':   return 'Authentication';
    case 'M1':     return 'M1 · ABHA Verification';
    case 'M2':     return 'M2 · Care Context Linking';
    case 'M3':     return 'M3 · Consent & Data Pull';
    case 'ADMIN':  return 'Admin / platform';
    case 'TENANT': return 'Tenant administration';
    default:       return m;
  }
}

/**
 * Best-effort guess at which preset the existing rules match. If every
 * api_master row is overridden ALLOW for milestones in {AUTH, M1} and
 * DENY otherwise, we say "M1". Anything else falls back to CUSTOM.
 */
function detectTier(rules: TenantApiAccess[], catalog: ApiMaster[]): Tier {
  if (catalog.length === 0 || rules.length === 0) return 'CUSTOM';
  const byCode = new Map(rules.map((r) => [r.apiCode, r.access]));
  const matches = (allowed: Set<string>) =>
    catalog.every((r) => {
      const want = allowed.has(r.milestone) ? 'ALLOW' : 'DENY';
      return byCode.get(r.apiCode) === want;
    });
  if (matches(TIER_MILESTONES.FULL)) return 'FULL';
  if (matches(TIER_MILESTONES.M1_M3)) return 'M1_M3';
  if (matches(TIER_MILESTONES.M1)) return 'M1';
  return 'CUSTOM';
}
